/**
 * Split an assistant answer into BLOCKS - headings, list items, fenced code and paragraphs.
 *
 * <h2>Why this exists</h2>
 * The assistant was already writing structure: "**What the changes do**" as a heading, "- 57/58 -
 * ..." as a list. Nothing rendered it. Every answer arrived as one uninterrupted run of text
 * inside `whitespace-pre-wrap`, so a reader had to parse the shape out of the prose themselves -
 * which is precisely the work formatting exists to remove.
 *
 * <h2>What the reading research actually says</h2>
 * Three findings drive this file, and each maps to one thing below:
 *
 *  1. People SCAN before they read. Usability work on web reading (Nielsen Norman Group) found
 *     that scannable text - meaningful subheads, bulleted lists, one idea per paragraph -
 *     measurably outperforms the same content as continuous prose. So headings and list items
 *     become real blocks with real spacing, not bold text in a wall.
 *
 *  2. Comfortable line length is roughly 45-75 characters (long-standing typographic guidance,
 *     Bringhurst among others). Past that the eye struggles to find the start of the next line on
 *     the return sweep. The measure is capped by the CALLER, not here - see RichText's wrapper -
 *     because only the caller knows how wide its column is.
 *
 *  3. Related items are easier to compare when they share a shape. A list of settings that all
 *     read "#id Name - old to new - why" can be compared down the column; the same content as
 *     sentences cannot, because every line starts somewhere different.
 *
 * <h2>Not every line here comes from the model</h2>
 * Worth being explicit, because it decides what is dead code and what is not: this parser runs
 * over EVERY row of the transcript, and the customer's own messages are among them. An answer has
 * already been through the server's filters - fenced code blocks are replaced wholesale and every
 * line's leading indentation is stripped (AssistantService.stripToken) - but a question the
 * customer typed or pasted has been through none of that. So the indentation and fence handling
 * below is not speculative: it is what renders a pasted log or an indented list back to the person
 * who pasted it.
 *
 * <h2>Deliberately tiny</h2>
 * This is NOT a markdown parser and must not become one. It recognises five things, all of which
 * appear in real transcripts, and treats everything else as a paragraph. A full parser would be a
 * much larger attack surface for model output, for no reader benefit.
 *
 * @param {string} text raw answer text
 * @returns {Array<{type:'heading'|'item'|'code'|'para', value:string}>} never null
 */

/** An opening or closing ``` on a line of its own (any language tag is discarded with it). */
const FENCE = /^\s*```/;

export function toBlocks(text) {
  if (!text || typeof text !== 'string') {
    return [];
  }
  const out = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');

  // Prose lines waiting to be split into paragraphs, and the code lines gathered since an opening
  // fence. Exactly one of the two is being filled at any moment.
  let prose = [];
  let fenced = null;

  const flushProse = () => {
    if (prose.length) {
      proseBlocks(prose.join('\n'), out);
      prose = [];
    }
  };

  const flushFence = () => {
    const body = fenced.join('\n');
    // An empty fence is markup with nothing in it; rendering an empty <pre> would just be a gap.
    if (body.trim()) {
      out.push({ type: 'code', value: body });
    }
    fenced = null;
  };

  /*
   * Fences are found BEFORE paragraphs are split, not after. A pasted stack trace or log routinely
   * contains a blank line, and splitting on blank lines first would cut the block in half and hand
   * each half to the inline tokenizer, which would then read `**` in a log line as emphasis.
   */
  lines.forEach((raw) => {
    if (fenced === null) {
      if (FENCE.test(raw)) {
        flushProse();
        fenced = [];
        return;
      }
      prose.push(raw);
      return;
    }
    if (FENCE.test(raw)) {
      flushFence();
      return;
    }
    fenced.push(raw);
  });
  // An UNCLOSED fence is the common failure - a reply cut short at the token limit, or someone
  // pasting the opening of a log. Its contents are still the customer's text, so they are shown
  // as code rather than dropped.
  if (fenced !== null) {
    flushFence();
  }
  flushProse();

  return out;
}

/** Headings, list items and paragraphs, for one run of text with no fences in it. */
function proseBlocks(text, out) {
  // Paragraphs are separated by a blank line; single newlines inside one are soft wraps that the
  // model uses freely, so they are joined rather than preserved.
  const chunks = text.split(/\n\s*\n/);

  chunks.forEach((chunk) => {
    const lines = chunk.split('\n');
    let para = [];

    const flush = () => {
      const joined = para.join(' ').trim();
      if (joined) {
        out.push({ type: 'para', value: joined });
      }
      para = [];
    };

    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) {
        flush();
        return;
      }
      // "## Heading" or "### Heading"
      const hash = line.match(/^#{1,4}\s+(.*)$/);
      if (hash) {
        flush();
        out.push({ type: 'heading', value: hash[1].trim() });
        return;
      }
      // A whole line that is nothing but bold text acts as a heading. The ** markers are KEPT so
      // the inline renderer still emits <strong>: such a line is sometimes a section title and
      // sometimes a bold step ("**1. Appium server isn't running.**"), and both want the emphasis
      // as well as the spacing. The block only adds the spacing.
      if (/^\*\*[^*]+\*\*:?$/.test(line)) {
        flush();
        out.push({ type: 'heading', value: line });
        return;
      }
      /*
       * "- item", "* item", "1. item".
       *
       * Deliberately ahead of the continuation rule below, which means an INDENTED bullet becomes
       * an item of its own rather than being folded into its parent. That flattens one level of
       * nesting, and it is the better of the two available wrongs: this parser has no nesting to
       * flatten INTO, so the alternative is running "- parent" and "- child" together into a
       * single line, which loses the child as a separate point rather than only its indent.
       */
      const bullet = line.match(/^(?:[-*•]|\d{1,2}[.)])\s+(.*)$/);
      if (bullet) {
        flush();
        out.push({ type: 'item', value: bullet[1].trim() });
        return;
      }
      // A continuation line of the item above: indented, and we are mid-list. Only reachable for
      // text the server has not run stripToken over - see the header note about whose lines these
      // are - which in practice means the customer's own messages.
      if (/^\s/.test(rawLine) && out.length && out[out.length - 1].type === 'item' && !para.length) {
        out[out.length - 1].value += ' ' + line;
        return;
      }
      para.push(line);
    });
    flush();
  });
}

export default toBlocks;
